/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-controller-access-in-routes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import EffortJudgement from '@linc-technologies/linced-common/enums/effort-judgement';
import { getProgressionJudgementLabel } from '@linc-technologies/linced-common/helpers/progression-judgement-label';
import { getEffortJudgementLabel } from '@linc-technologies/linced-common/helpers/effort-judgement-label';
import {
  getSubjectData,
  getVisibleSubjects,
} from '@linc-technologies/linced-common/utils/reports';
import { uniq } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import config from 'linced-app/config/environment';
import moment from 'moment';

export default class IndexRoute extends Route {
  queryParams = {
    reportDate: {
      refreshModel: true,
    },
  };

  // Services
  @service
  dates;

  @service
  fetch;

  @service
  heroApi;

  @service
  options;

  @service
  session;

  @service
  store;

  @alias('session.school.jurisdiction')
  jurisdiction;

  ProgressionJudgement = ProgressionJudgement;
  getProgressionJudgementLabel = getProgressionJudgementLabel;
  EffortJudgement = EffortJudgement;
  getEffortJudgementLabel = getEffortJudgementLabel;

  model(params) {
    let personModel = this.modelFor('people.person');
    let academicYear = this.get('dates.rolloverDate');
    let person = personModel.person;
    let startTime = moment()
      .set({ month: 0, date: 1, hour: 0, minute: 0, second: 0 })
      .unix();
    let endTime;
    let endTimeMillis;
    let rolloverTimeMillis = moment(this.dates.rolloverDate).unix() * 1000;
    let dateFrom = academicYear;
    let dateTo = moment().format('YYYY-MM-DD');

    if (
      !isEmpty(params.reportDate) &&
      moment().format('YYYY-MM-DD') !== params.reportDate
    ) {
      let reportDate = moment(params.reportDate);
      startTime = moment(reportDate)
        .set({ month: 0, date: 1, hour: 0, minute: 0, second: 0 })
        .unix();
      endTime = reportDate.unix();
      endTimeMillis = endTime * 1000;
      rolloverTimeMillis =
        moment(this.dates.previousRolloverDate(reportDate)).unix() * 1000;
      dateFrom = this.dates.previousRolloverDate(reportDate);
      dateTo = reportDate.format('YYYY-MM-DD');
    }

    let promises = {
      person,
      badges: personModel.badges,
      // Resolved to a plain array: the {{sort-by}} template helper calls
      // A() on it, which throws on ember-data's query-result live array.
      pages: this.store
        .query('posts/page', {
          visibleOnStudentProfile: true,
          learnerId: person.id,
        })
        .then((pages) => pages.slice()),
      posts: this.store.query('posts/post', {
        personId: person.id,
        visibleOnStudentProfile: 1,
        startTime,
        endTime,
        include: 'reactions,comments',
      }),
      attendanceRecords: this.store.query('attendance/record', {
        personId: person.id,
        absencesOnly: true,
        dateFrom: dateFrom,
        dateTo: dateTo,
        jurisdiction: this.session.school.jurisdiction,
      }),
      goalSubjects: this.store.findAll('goals/subject'),
      developingStates: this.store.query('goals/state', {
        current: true,
        personId: person.id,
        awarded: GoalState.DEVELOPING,
        include: 'goals',
        asAtTimeMillis: endTimeMillis,
      }),
      secureStates: this.store.query('goals/state', {
        current: true,
        personId: person.id,
        awarded: GoalState.SECURE,
        include: 'goals',
        asAtTimeMillis: endTimeMillis,
        fromTimeMillis: rolloverTimeMillis,
      }),
      // Resolved to a plain array: see pages above.
      plpGoals: this.store
        .query('goals/state', {
          current: true,
          personId: person.id,
          awarded: GoalState.DEVELOPING,
          include: 'goals',
          isPLP: true,
          asAtTimeMillis: endTimeMillis,
        })
        .then((states) => states.slice()),
      // levels, categories, subcategories and goals are required for
      // generating progress charts and details.
      categories: this.store.findAll('goals/category'),
      goals: this.store.findAll('goals/goal'),
      levels: this.store.findAll('milestones/level'),
      milestones: this.store.query('milestones/milestone', {
        progressionType: ProgressionType.PROGRESSION,
      }),
      milestoneSets: this.store.query('milestones/milestone-set', {
        include: 'milestones',
      }),
      subcategories: this.store.findAll('goals/subcategory'),
      terms: this.store.query('term', {
        schoolId: this.session.activeSchool,
      }),
      timeframes: this.store
        .query('timeframe', {
          schoolId: this.session.activeSchool,
        })
        .then((timeframes) => {
          return timeframes
            .slice()
            .sort((a, b) => a.startDateUnix - b.startDateUnix);
        }),
    };

    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      promises.dayCodes = this.store.query('attendance/nz-moe-day', {
        personId: person.id,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
    }

    if (person.type === PersonType.LEARNER) {
      let jurisdiction =
        this.get('session.school.jurisdiction') ?? Jurisdiction.NZ_MOE;
      if ([Jurisdiction.NZ_MOE, Jurisdiction.AU_NSW].includes(jurisdiction)) {
        let schoolId = this.session.activeSchool;
        let params = `jurisdiction=${jurisdiction}&schoolId=${schoolId}&personId=${person.id}&startTime=${startTime}`;
        if (!isEmpty(endTime)) {
          params = `${params}&endTime=${endTime}`;
        }
        let url = `${config.APP.apiHost}/attendance/v4/learnersummary/nzmoe?${params}`;
        promises.attendanceSummary = this.fetch
          .request(url)
          .then((resp) => resp.summary);
      }
    }

    // Fetch report media
    let mediaId = this.options.value('parents:reportPage:reportMedia');
    if (!isEmpty(mediaId)) {
      promises.reportMedia = this.store.findRecord('media', mediaId);
    }

    promises.school = this.session.school;

    return hash(promises);
  }

  afterModel(model) {
    let endTime;
    let params = this.paramsFor(this.routeName);
    if (
      !isEmpty(params.reportDate) &&
      moment().format('YYYY-MM-DD') !== params.reportDate
    ) {
      endTime = moment(params.reportDate).unix();
    }
    let assessments = uniq(
      model.pages
        .filter((page) => page.hasAssessments)
        .reduce((ids, page) => {
          ids.push(...page.assessment_ids);
          return ids;
        }, []),
    );
    if (assessments.length) {
      let assessmentIds = JSON.stringify([...assessments]);

      model.assessments = this.store.query('assessments/assessment', {
        assessmentIds,
      });
      model.modules = this.store.query('assessments/module', {
        assessmentIds,
        include: 'gradeSchedules',
      });
      model.grades = this.store.query('assessments/grade', {
        assessmentIds,
        personId: model.person.id,
        asAtTime: endTime,
      });
    }

    // Add subject-specific data to the model.
    const visibleSubjects = getVisibleSubjects(model.goalSubjects, model.pages);
    Object.assign(
      model,
      getSubjectData(this, model.person.id, visibleSubjects, endTime),
    );

    return hash(model).then((resolved) => {
      // Replace the promise properties assigned above with their resolved
      // values; leaving them as promises means any later access (e.g.
      // isEmpty(model.grades)) reads the promise, not its value.
      Object.assign(model, resolved);

      // Tell the controller that we're done loading, in case this was a Time Warp.
      let controller = this.controllerFor('people.person.index');
      if (!isEmpty(controller)) {
        controller.set('timeWarpLoading', false);
      }
    });
  }

  resetController(controller) {
    controller.reset();
  }

  @action
  reloadModel() {
    this.refresh().then(() => {
      this.controller.set('loadingDatePeriod', false);
    });
  }
}
